import { useMemo } from "react";
import { useQuery, useStatus } from "@powersync/react";
import { db } from "./db";

export type SourceFilter = "all" | "dine-in" | "pickup";

export interface TicketItem {
  id: string;
  name: string;
  variantLabel: string | null;
  notes: string | null;
  qty: number;
  /** The kitchen finished this item (or the whole ticket was marked ready). */
  ready: boolean;
  /** The till already carried it out; it can't be un-ticked. */
  served: boolean;
}

export type OrderType = "counter" | "dine_in" | "pickup" | "online";
export type OrderSource = "pos" | "guest_portal" | "mobile_app" | "phone" | "walk_in";

export interface Ticket {
  id: string;
  orderNumber: string;
  orderType: OrderType;
  source: OrderSource;
  isDineIn: boolean;
  tableNumber: number | null;
  customerName: string | null;
  serverName: string | null;
  /** sales.orders.notes — guest portal's "Special requests" or the POS order note. */
  orderNotes: string | null;
  kitchenStatus: "new" | "preparing" | "ready";
  /** The till flagged this ticket as running late. */
  nudged: boolean;
  /** A tap on this ticket hasn't reached the server yet (offline or in flight). */
  pendingSync: boolean;
  createdAt: string;
  items: TicketItem[];
}


interface ActiveTicketRow {
  id: string;
  status: Ticket["kitchenStatus"];
  pending_sync: number;
  nudged_at: string | null;
  created_at: string;
  order_number: string;
  order_type: OrderType;
  source: OrderSource;
  table_number: number | null;
  customer_name: string | null;
  server_name: string | null;
  notes: string | null;
  items: string;
}

const ACTIVE_TICKETS_QUERY = `
  SELECT kt.id, kt.status, kt.created_at, kt.nudged_at,
    EXISTS (SELECT 1 FROM ps_crud c WHERE json_extract(c.data, '$.id') = kt.id) AS pending_sync,
    o.order_number, o.order_type, o.source, tb.table_number, o.customer_name, st.name AS server_name, o.notes,
    (SELECT json_group_array(json_object('id', i.id, 'product_name', i.product_name, 'variant_label', i.variant_label,
            'quantity', i.quantity, 'notes', i.notes, 'ready_at', i.ready_at, 'served_at', i.served_at, 'option_labels', json(COALESCE(i.option_labels, '[]'))))
       FROM (SELECT * FROM order_items WHERE kitchen_ticket_id = kt.id AND is_voided = 0 ORDER BY created_at, id) i) AS items
  FROM kitchen_tickets kt
  JOIN orders o ON o.id = kt.order_id
  LEFT JOIN tables tb ON tb.id = o.table_id
  LEFT JOIN staff st ON st.id = o.staff_id
  WHERE kt.store_id = ? AND kt.status IN ('new', 'preparing', 'ready')
    AND EXISTS (SELECT 1 FROM order_items oi WHERE oi.kitchen_ticket_id = kt.id AND oi.is_voided = 0 AND oi.served_at IS NULL)
  ORDER BY kt.created_at ASC
`;


const NEXT = { new: "preparing", preparing: "ready", ready: "done" } as const;
const PREV = { preparing: "new", ready: "preparing", done: "ready" } as const;

function setStatus(id: string, status: string) {
  const now = new Date().toISOString();
  void db.execute(`UPDATE kitchen_tickets SET status = ?, done_at = ?, updated_at = ? WHERE id = ?`, [status, status === "done" ? now : null, now, id]);
}

function setItemsReady(ids: string[], ready: boolean) {
  if (!ids.length) return;
  const now = new Date().toISOString();
  // Only items whose ready state changes: an item already in that state would upload as a change of
  // updated_at alone, which sync-api refuses ("no_changes").
  void db.execute(
    `UPDATE order_items SET ready_at = ?, updated_at = ? WHERE id IN (${ids.map(() => "?").join(", ")}) AND ready_at IS ${ready ? "" : "NOT "}NULL`,
    [ready ? now : null, now, ...ids],
  );
}

/**
 * Live kitchen tickets for one store, straight from PowerSync's local SQLite. A tap is a local
 * UPDATE: the board changes instantly, PowerSync queues the change and uploads it (retrying while
 * offline), and the server's copy wins if it refuses.
 */
export function useKitchenTickets(storeId: string | undefined) {
  const active = useQuery<ActiveTicketRow>(ACTIVE_TICKETS_QUERY, [storeId ?? ""]);
  const { hasSynced } = useStatus();

  const tickets = useMemo<Ticket[]>(
    () =>
      active.data.map((row) => ({
        id: row.id,
        orderNumber: row.order_number,
        orderType: row.order_type,
        source: row.source,
        isDineIn: row.order_type === "dine_in",
        tableNumber: row.table_number,
        customerName: row.customer_name,
        serverName: row.server_name,
        orderNotes: row.notes?.trim() || null,
        kitchenStatus: row.status,
        nudged: !!row.nudged_at,
        pendingSync: !!row.pending_sync,
        createdAt: row.created_at,
        items: (JSON.parse(row.items || "[]") as Array<{ id: string; product_name: string; variant_label: string | null; quantity: number; notes: string | null; ready_at: string | null; served_at: string | null; option_labels: string[] }>).map((item) => {
          const optionLabels = item.option_labels.map((l) => l.trim()).filter(Boolean);
          return {
            id: item.id,
            name: item.product_name,
            variantLabel: item.variant_label?.trim() || (optionLabels.length ? optionLabels.join(", ") : null),
            notes: item.notes?.trim() || null,
            qty: item.quantity,
            ready: !!item.ready_at || !!item.served_at || row.status === "ready",
            served: !!item.served_at,
          };
        }),
      })),
    [active.data],
  );


  const advance = (id: string) => {
    const t = tickets.find((x) => x.id === id);
    if (!t) return;
    // "Mark the rest ready": every item gets its tick with the ticket.
    if (t.kitchenStatus === "preparing") setItemsReady(t.items.filter((i) => !i.ready).map((i) => i.id), true);
    setStatus(id, NEXT[t.kitchenStatus]);
  };
  const recall = (id: string) => {
    const t = tickets.find((x) => x.id === id);
    if (!t || t.kitchenStatus === "new") return;
    const status = t.kitchenStatus;
    // Back out of ready keeps every tick, so the cook un-ticks only what isn't done. Back to new clears
    // them, except on what the floor already served.
    setItemsReady(t.items.filter((i) => !i.served).map((i) => i.id), status === "ready");
    setStatus(id, PREV[status]);
  };
  /** One item done (or not, on a second tap). The ticket follows: cooking once anything is ticked, ready once everything is. */
  const toggleItem = (id: string, itemId: string) => {
    const t = tickets.find((x) => x.id === id);
    const item = t?.items.find((i) => i.id === itemId);
    if (!t || !item || item.served) return;
    const next = !item.ready;
    setItemsReady([itemId], next);
    const allReady = t.items.every((i) => (i.id === itemId ? next : i.ready));
    if (t.kitchenStatus === "ready" && !next) {
      // the ticket was ready as a whole: keep the ticks of the other items when one is taken back
      setItemsReady(t.items.filter((i) => i.id !== itemId && !i.served).map((i) => i.id), true);
      setStatus(id, "preparing");
    } else if (allReady) setStatus(id, "ready");
    else if (t.kitchenStatus === "new") setStatus(id, "preparing");
  };

  return { tickets, advance, recall, toggleItem, ready: !!hasSynced && !active.isLoading };
}
